<script lang="ts">
  import type { Position } from "$types";
  import { fade } from "svelte/transition";
  import { Map, Marker } from "@beyonk/svelte-mapbox";
  import MapMarker from "$lib/components/svg/MapMarker.svelte";
  import type { SvelteComponent } from "svelte";
  import Spinner from "$components/forms/Spinner.svelte";

  export let markers: [number, number][];

  // geo calculations
  import {
    lineString,
    type BBox,
    type LineString,
    type Feature,
    type Point,
  } from "@turf/helpers";
  import bbox from "@turf/bbox";
  import centerOfMass from "@turf/center-of-mass";

  // loading thing
  let ready: boolean = false;

  // set a bounding box using the array of markers, requires more than one marker
  let line: Feature<LineString>, bounds: BBox, bbox_center: Feature<Point>;
  if (markers.length > 1) {
    line = lineString(markers);
    bounds = bbox(line);
    bbox_center = centerOfMass(line);
  }

  let zoom: number = 1;
  let mapComponent: SvelteComponent;

  // 3rd party style
  let style = "mapbox://styles/stephengunn/ckxaplywn1q0u14pp9yg16n0y";
  const reposition = (center: Position) => {
    mapComponent.flyTo({
      center,
      speed: 1, // default to match the drop pin speeds
      curve: 1,
      zoom,
    });
  };

  const load_map = () => {
    ready = true;
    if (markers.length > 1) {
      mapComponent.fitBounds(bounds, {
        padding: 50,
        speed: 1,
      });
    } else {
      reposition(markers[0]);
    }
  };
  let w: number;
</script>

<svelte:window bind:innerWidth={w} />
{#if !ready}
  <div class="loading" out:fade={{ delay: 200, duration: 1000 }}>
    <Spinner />
  </div>
{/if}
<Map
  bind:zoom
  bind:style
  accessToken="pk.eyJ1Ijoic3RlcGhlbmd1bm4iLCJhIjoiY2tuZXQycTVsMDU5ejJ3cDZlNHc0a2Z2biJ9.aFQPUGvNxK1As7Foy495-A"
  bind:this={mapComponent}
  options={{
    scrollZoom: false,
    maxZoom: 2,
    zoom,
    interactive: false,
    center: bbox_center?.geometry?.coordinates ?? markers[0],
    attributionControl: false,
  }}
  on:ready={load_map}
>
  {#each markers as marker, index}
    <Marker
      lng={marker[0]}
      lat={marker[1]}
      popup={false}
      markerOptions={{
        anchor: "bottom",
        offset: [0, w < 700 ? 5 : 10],
      }}
    >
      {#if ready}
        <MapMarker
          height="{w < 700 ? 14 : 28}px"
          width="{w < 700 ? 25 : 50}px"
          delay={40 * index + 1500}
        />
      {/if}
    </Marker>
  {/each}
</Map>

<style>
.loading {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: grid;
  place-content: center;
  z-index: 2;
  background-color: #75cff0;
  background-image: url("data:image/svg+xml,%3Csvg width='6' height='6' viewBox='0 0 6 6' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%234eb0d4' fill-opacity='0.4' fill-rule='evenodd'%3E%3Cpath d='M5 0h1L0 6V5zM6 5v1H5z'/%3E%3C/g%3E%3C/svg%3E");
  color: white;
  font-weight: bold;
  font-size: 1rem;
}
</style>
